import { useMemo, useCallback } from 'react'
import {
  faCheck,
  faTrashCan,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons'
import { noop } from 'lodash'

import { PlaidItem } from '@/reducers/finances/plaidItemReducer'
import { selectVisibleAccounts } from '@/selectors/financeSelectors'
import { getIsRequiredStepsOnboarding } from '@/selectors/user.selectors'
import { deletePlaidItem } from '@/actions/financialAccountActions'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  AlertDialog,
  Banner,
  BannerProps,
  BannerVariants,
  Button,
  Card,
  Icon,
  Loader,
  Separator,
  Skeleton,
  Text,
  Toggle,
} from '@/ui'
import { InstitutionLogo } from './logo'
import { TextColors, TypographyStyles } from '@/styles'
import { FinancialAccountPrimaryUse } from '@/reducers/finances/financialAccountsReducer'
import { PrimaryUseAnswer } from '@/features/onboarding/connect-stage/types'
import { OnboardingTaskIdents } from '@/features/onboarding/config'
import { getUserActionItemByActionItemIdentifier } from '@/features/user/dashboard/user-action-items/lib/userActionItems.selectors'
import { updateUserActionItem } from '@/features/user/dashboard/user-action-items/lib/userActionItems.slice'
import {
  PlaidItemSyncStatus,
  retryPlaidSync,
  SyncErrorClassification,
  TransactionSyncStatus,
} from '@/actions/plaid/authActions'
import { PlaidLink } from '@/components/plaid/PlaidLink'
import { SYNC_ERROR_MESSAGES } from '@/components/plaid/syncErrorMessages'
const capitalOneInstitutionId = 'ins_128026'

export const LoadingOnboardingInstitutionCard = () => (
  <Card className="flex flex-col gap-6">
    <Skeleton className="h-30" />
    <Skeleton className="mt-4" />
    <Skeleton className="mt-4" />
  </Card>
)

const PRIMARY_USE_OPTIONS: {
  value: FinancialAccountPrimaryUse
  label: string
}[] = [
  { value: FinancialAccountPrimaryUse.BUSINESS, label: 'Business' },
  { value: FinancialAccountPrimaryUse.PERSONAL, label: 'Personal' },
]

const YES_NO_OPTIONS: { value: boolean; label: string }[] = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
]

const AccountPrimaryUseRow = ({
  account,
  answer,
  showErrors,
  onSelectPrimaryUse,
  onSelectHasOtherType,
}: {
  account: { id: number; name: string; mask: string | null }
  answer: PrimaryUseAnswer | undefined
  showErrors: boolean
  onSelectPrimaryUse: (primaryUse: FinancialAccountPrimaryUse) => void
  onSelectHasOtherType: (hasOtherType: boolean) => void
}) => (
  <div className="flex flex-col gap-2 mt-4">
    <div className="flex items-center justify-between gap-4">
      <div className="flex flex-col">
        <Text variant={TypographyStyles.BODY_SM}>{account.name}</Text>
        <Text variant={TypographyStyles.BODY_XS}>•••• {account.mask}</Text>
      </div>
      <div className="flex items-center gap-2">
        {PRIMARY_USE_OPTIONS.map((option) => (
          <Toggle
            key={option.value}
            variant="outline"
            size="lg"
            checked={answer?.primaryUse === option.value}
            onCheckedChange={() => onSelectPrimaryUse(option.value)}
          >
            {option.label}
          </Toggle>
        ))}
      </div>
    </div>

    {showErrors && !answer?.primaryUse && (
      <Text variant={TypographyStyles.BODY_XS} color={TextColors.RED}>
        Select Business or Personal to continue.
      </Text>
    )}

    {answer?.primaryUse && (
      <div className="flex flex-col gap-2">
        <Text variant={TypographyStyles.LABEL_XS}>
          {answer.primaryUse === FinancialAccountPrimaryUse.BUSINESS
            ? 'Any personal charges included too?'
            : 'Any business charges included too?'}
        </Text>
        <div className="flex items-center gap-2">
          {YES_NO_OPTIONS.map((option) => (
            <Toggle
              key={String(option.value)}
              variant="outline"
              size="sm"
              checked={answer.hasOtherType === option.value}
              onCheckedChange={() => onSelectHasOtherType(option.value)}
            >
              {option.label}
            </Toggle>
          ))}
        </div>
        {showErrors && answer.hasOtherType === null && (
          <Text variant={TypographyStyles.BODY_XS} color={TextColors.RED}>
            Select Yes or No to continue.
          </Text>
        )}
      </div>
    )}
  </div>
)

export const OnboardingInstitutionCard = ({
  institution,
  hasOtherAccounts,
  primaryUseAnswers = {},
  showPrimaryUseErrors = false,
  onSelectPrimaryUse = noop,
  onSelectHasOtherType = noop,
  syncStatus,
  restartSync,
}: {
  institution: PlaidItem
  hasOtherAccounts: boolean
  primaryUseAnswers?: Record<number, PrimaryUseAnswer>
  /** Set once the stage has blocked Continue on an unanswered account. */
  showPrimaryUseErrors?: boolean
  /** Owned by the onboarding stage so analytics stay out of this shared card. */
  onSelectPrimaryUse?: (
    financialAccountId: number,
    primaryUse: FinancialAccountPrimaryUse
  ) => void
  onSelectHasOtherType?: (
    financialAccountId: number,
    hasOtherType: boolean
  ) => void
  syncStatus: PlaidItemSyncStatus | null
  restartSync: () => void
}) => {
  const { institutionName } = institution
  const dispatch = useAppDispatch()

  const visibleAccounts = useReselector(
    selectVisibleAccounts,
    institution.id,
    false
  )

  const retryable =
    syncStatus?.syncErrorClassification ===
    SyncErrorClassification.TRANSIENT_RETRYABLE

  const isRequiredStepsOnboarding = useReselector(getIsRequiredStepsOnboarding)

  const connectBusinessOnboardingItem = useReselector(
    getUserActionItemByActionItemIdentifier,
    OnboardingTaskIdents.CONNECT_A_BUSINESS_BANK_ACCOUNT
  )

  const handleRemoveConnection = async () => {
    // Adding an account auto completes the connect onboarding item but we need to uncomplete it if all accounts removed
    if (!hasOtherAccounts) {
      if (connectBusinessOnboardingItem?.completedAt) {
        await dispatch(
          updateUserActionItem(connectBusinessOnboardingItem.id, {
            id: connectBusinessOnboardingItem.id,
            completedAt: null,
          })
        )
      }
    }
    await dispatch(deletePlaidItem(institution.id))
  }

  const retrySync = useCallback(async () => {
    await dispatch(retryPlaidSync(institution.id))
    // Restart polling in parent
    restartSync()
  }, [dispatch, institution.id, restartSync])

  const syncDisplay = useMemo(() => {
    switch (syncStatus?.transactionSyncStatus) {
      case TransactionSyncStatus.CONNECTING:
      case TransactionSyncStatus.SYNCING:
        return (
          <>
            <div>
              <Loader />
            </div>
            <Text className="ml-1" variant={TypographyStyles.BODY_XS}>
              Syncing
            </Text>
          </>
        )
      case TransactionSyncStatus.IDLE:
        return (
          <>
            <Icon icon={faCheck} />
            <Text className="ml-1" variant={TypographyStyles.BODY_XS}>
              Synced
            </Text>
          </>
        )

      case TransactionSyncStatus.ERROR:
        return (
          <>
            <Icon icon={faTriangleExclamation} color="text-red-200" />
            {retryable && (
              <Button
                variant="link"
                className="pl-1 pr-0 text-red-200"
                onClick={retrySync}
              >
                Retry
              </Button>
            )}
          </>
        )
      case undefined:
      default:
        return null
    }
  }, [retrySync, retryable, syncStatus?.transactionSyncStatus])

  const importSummary = useMemo(() => {
    const importedCount = syncStatus?.transactionsSyncedCount ?? 0
    const status = syncStatus?.transactionSyncStatus
    const syncPending =
      !status ||
      status === TransactionSyncStatus.CONNECTING ||
      status === TransactionSyncStatus.SYNCING

    if (importedCount === 0 && syncPending) {
      return 'Importing transactions…'
    }

    return `${importedCount} transactions imported`
  }, [syncStatus?.transactionsSyncedCount, syncStatus?.transactionSyncStatus])

  const plaidAlert = useMemo(() => {
    if (syncStatus?.transactionSyncStatus === TransactionSyncStatus.ERROR) {
      const bannerProps: BannerProps = retryable
        ? {
            variant: BannerVariants.DANGER,
            wrapperClassName: 'mt-4',
            ctaText: 'Retry',
            onClick: retrySync,
          }
        : { variant: BannerVariants.DANGER, wrapperClassName: 'mt-4' }

      const classification =
        syncStatus?.syncErrorClassification ??
        SyncErrorClassification.UNKNOWN_PLAID_ERROR
      const message = SYNC_ERROR_MESSAGES[classification]
      const showReconnect =
        message.action === 'reconnect' && visibleAccounts.length > 0

      return (
        <Banner {...bannerProps}>
          <b>{message.title}.</b> {message.message}
          {showReconnect && (
            <>
              {' '}
              <PlaidLink
                className="p-0 h-auto"
                buttonVariant="link"
                reconnectButtonText="Reconnect"
                account={visibleAccounts[0]}
                redirect={false}
                disconnected
              />
            </>
          )}
        </Banner>
      )
    }

    if (institution.institutionId === capitalOneInstitutionId) {
      return (
        <Banner wrapperClassName="mt-4">
          <b>{institutionName} has data restrictions.</b> This means we may need
          you to upload past statements to accurately reconstruct and reconcile
          your books for initial set-up.
        </Banner>
      )
    }

    return null
  }, [
    institution.institutionId,
    institutionName,
    retrySync,
    retryable,
    syncStatus?.syncErrorClassification,
    syncStatus?.transactionSyncStatus,
    visibleAccounts,
  ])

  return (
    <Card className="flex flex-col gap-6">
      <div className="flex flex-row justify-between items-center">
        <InstitutionLogo institution={institution} width={24} />
        <Text variant={TypographyStyles.H3} className="flex-1 ml-3">
          {institutionName}
        </Text>
        {syncDisplay}
        {isRequiredStepsOnboarding && (
          <AlertDialog>
            <AlertDialog.Trigger asChild>
              <Icon icon={faTrashCan} className="ml-2 cursor-pointer" />
            </AlertDialog.Trigger>
            <AlertDialog.Content>
              <AlertDialog.Header>
                <AlertDialog.Title>Are you sure?</AlertDialog.Title>
                <AlertDialog.Description>
                  This account connection will be permanently removed. To
                  reconnect, you&apos;ll need to re-enter your login
                  credentials.
                </AlertDialog.Description>
              </AlertDialog.Header>
              <AlertDialog.Footer>
                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                <AlertDialog.Action onClick={handleRemoveConnection}>
                  Remove
                </AlertDialog.Action>
              </AlertDialog.Footer>
            </AlertDialog.Content>
          </AlertDialog>
        )}
      </div>
      <Text variant={TypographyStyles.BODY_SM} className="ml-9">
        {importSummary}
      </Text>
      <div className="ml-9">
        <Separator />
      </div>

      {visibleAccounts.length > 0 && (
        <div className="ml-9">
          {visibleAccounts.map((account) => (
            <AccountPrimaryUseRow
              key={account.id}
              account={account}
              answer={primaryUseAnswers?.[account.id]}
              showErrors={showPrimaryUseErrors}
              onSelectPrimaryUse={(primaryUse) =>
                onSelectPrimaryUse(account.id, primaryUse)
              }
              onSelectHasOtherType={(hasOtherType) =>
                onSelectHasOtherType(account.id, hasOtherType)
              }
            />
          ))}
        </div>
      )}

      {plaidAlert}
    </Card>
  )
}
